export interface Cashbox {
    _id: string;
    /**
     * 1 (o ausente): cierre viejo, un solo monto en dólares sobre la cuenta de efectivo.
     * 2: cierre por método de pago, cada cuenta en su moneda (ver entries).
     */
    schemaVersion?: number;
    initialAmountUSD: number;
    currentAmountUSD?: number;
    finalAmountUSD?: number;
    status: 'open' | 'closed';
    openingDate: Date;
    closingDate?: Date;
    openedBy: {
        _id: string;
        name: string;
    };
    closedBy?: {
        _id: string;
        name: string;
    };
    sales: Array<{
        pmId: string;
        count: number;
    }>;
    totalSales: number;
    bankAccounts?: Array<{
        bankAccountId: string;
        bankAccountName: string;
        ventas: number;
        abonos: number;
        gastos: number;
        totalUSD: number;
        isCashAccount: boolean;
    }>;
    paymentMethods?: Array<CashboxPaymentMethod>;

    /** Solo en cierres v2: una entrada por cuenta participante. */
    entries?: Array<CashboxEntry>;
    /** Solo en cierres v2: diferencia total por moneda, sin mezclarlas. */
    differenceTotals?: CashboxDifferenceTotals;
    /** Ventas de la jornada que quedaron fuera por pagarse con métodos que no participan. */
    excludedSalesCount?: number;

    discrepancy?: number;
    observations?: string;
    tenantId: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface CashboxDifferenceTotals {
    USD: number;
    VES: number;
    [currency: string]: number;
}

/**
 * Una cuenta participante del cierre. Los montos esperado, contado y diferencia solo
 * existen después de cerrar: durante el día el conteo es a ciegas.
 */
export interface CashboxEntry {
    bankAccountId: string;
    bankAccountName: string;
    /** Nombre del método de pago, o de todos los que comparten la cuenta. */
    displayName: string;
    currency: string;
    isCash: boolean;
    paymentMethods: Array<{ pmId: string; name: string; typeCode?: string }>;
    initialAmount: number;
    expectedAmount?: number;
    countedAmount?: number;
    difference?: number;
    /** Resumen de la jornada. La apertura no cuenta como entrada. */
    inflowsCount?: number;
    inflowsTotal?: number;
    outflowsCount?: number;
    /** Viene en negativo, tal como se muestra. */
    outflowsTotal?: number;
    movements?: Array<CashboxMovement>;
    movementsTruncated?: boolean;
}

/** Movimiento que afectó una cuenta durante la jornada. El monto viene firmado. */
export interface CashboxMovement {
    registryId: string;
    date: Date;
    concept: string;
    movementType: string;
    pmName: string;
    billNumber: string;
    amount: number;
    converted?: boolean;
    exchangeRate?: number;
}

/** Método de pago tal como lo devuelve la configuración del cierre de caja. */
export interface CashboxConfigPaymentMethod {
    pmId: string;
    name: string;
    currencyCode: string;
    typeCode: string;
    bankAccountId: string;
    bankAccountName: string;
    isCash: boolean;
    /** El método quedó configurado pero ya no existe o se quedó sin cuenta bancaria. */
    missing: boolean;
}

/** Método elegible para el cierre: activo y con cuenta bancaria asociada. */
export interface CashboxAvailablePaymentMethod {
    pmId: string;
    name: string;
    currencyCode: string;
    typeCode: string;
    bankAccountId: string;
    bankAccountName: string;
    isCash: boolean;
    selected: boolean;
}

export interface CashboxConfig {
    enabled: boolean;
    /** Los que están configurados hoy, incluidos los que quedaron inválidos (missing). */
    paymentMethods: Array<CashboxConfigPaymentMethod>;
    /** Todos los que el comercio puede elegir, con su estado de selección. */
    availablePaymentMethods: Array<CashboxAvailablePaymentMethod>;
}

/** Cuentas disponibles para abrir caja, ya agrupadas por cuenta bancaria. */
export interface CashboxOpeningEntry {
    bankAccountId: string;
    bankAccountName: string;
    displayName: string;
    currency: string;
    isCash: boolean;
    paymentMethods: Array<{ pmId: string; name: string; typeCode?: string }>;
}

export interface CashboxOpeningInfo {
    enabled: boolean;
    entries: Array<CashboxOpeningEntry>;
    missingPaymentMethods: Array<CashboxConfigPaymentMethod>;
}

export interface CashboxPaymentMethod {
    _id: string;
    amount: number;
    baName: string;
    baId: string;
    baRegistries: Array<{
        _id: string;
        exchangeRate: number;
        amount: number;
    }>;
    count: number;
    currency: string;
    pmName: string;
}

/** Un cierre es del esquema nuevo cuando trae entradas por cuenta. */
export const isMultiMethodCashbox = (cashbox: Cashbox): boolean => cashbox?.schemaVersion === 2 || !!cashbox?.entries?.length;
